import { useEffect, useRef } from "react";

/**
 * True where the browser's own character-level undo should win. The properties pane,
 * the options editor, the Text block and the view name are all text entry, and someone
 * pressing Ctrl+Z after a typo wants the typo back, not their last block move reverted.
 */
function isTextEntry(target: EventTarget | null) {
  const element = target as HTMLElement | null;

  if (!element?.tagName) {
    return false;
  }

  // The attribute rather than `isContentEditable`, which jsdom does not compute, and
  // `closest` so a press inside a rich text area counts as being in it.
  return (
    ["INPUT", "TEXTAREA", "SELECT"].includes(element.tagName) ||
    Boolean(element.closest('[contenteditable]:not([contenteditable="false"])'))
  );
}

/**
 * Undo and redo on the platform's own shortcuts, while the designer is editable.
 * Ctrl+Y as well as Ctrl+Shift+Z, because Windows answers to both.
 */
export function useHistoryShortcuts(
  onUndo: () => void,
  onRedo: () => void,
  enabled = true
) {
  // In refs, so an inline callback doesn't resubscribe every render.
  const handlers = useRef({ onUndo, onRedo });
  useEffect(() => {
    handlers.current = { onUndo, onRedo };
  }, [onUndo, onRedo]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || isTextEntry(event.target)) {
        return;
      }

      const key = event.key.toLowerCase();

      if (key === "z" && !event.shiftKey) {
        event.preventDefault();
        handlers.current.onUndo();
      } else if ((key === "z" && event.shiftKey) || key === "y") {
        event.preventDefault();
        handlers.current.onRedo();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [enabled]);
}

export default useHistoryShortcuts;
